// src/services/media.service.ts
import api from '@/services/api';
import type { MediaDocument } from '@/types/media';

export interface DocumentParams {
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
  sortBy?: string;
  sortOrder?: string;
}

export interface UploadDocumentData {
  title: string;
  description?: string;
  file: File;
}

export interface UpdateDocumentData {
  title?: string;
  description?: string;
  file?: File;
}

export interface MediaResponse {
  success: boolean;
  data: MediaDocument[];
  pagination: {
    total: number;
    page: number;
    pages: number;
    limit: number;
  };
  message: string;
}

export interface SingleMediaResponse {
  success: boolean;
  data: MediaDocument;
  message: string;
}

export interface DeleteResponse {
  success: boolean;
  message: string;
}

class MediaService {
  async getDocuments(params?: DocumentParams): Promise<MediaResponse> {
    const response = await api.get('/api/media', { params });
    return response.data;
  }

  async getAllDocuments(params?: Omit<DocumentParams, 'page' | 'limit'>): Promise<MediaDocument[]> {
    try {
      const countResponse = await api.get('/api/media', {
        params: { ...params, limit: 1000, page: 1 }
      });

      const total = countResponse.data.pagination?.total || 0;

      if (total === 0) return [];

      const response = await api.get('/api/media', {
        params: { ...params, limit: total, page: 1 }
      });

      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching all documents:', error);
      throw error;
    }
  }

  async uploadDocument(data: UploadDocumentData): Promise<SingleMediaResponse> {
    const formData = new FormData();
    formData.append('title', data.title);
    if (data.description) formData.append('description', data.description);
    formData.append('file', data.file);

    const response = await api.post('/api/media/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async updateDocument(id: string, data: UpdateDocumentData): Promise<SingleMediaResponse> {
    const formData = new FormData();
    if (data.title) formData.append('title', data.title);
    if (data.description !== undefined) formData.append('description', data.description);
    if (data.file) formData.append('file', data.file);

    const response = await api.put(`/api/media/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async deleteDocument(id: string): Promise<DeleteResponse> {
    const response = await api.delete(`/api/media/${id}`);
    return response.data;
  }

  // Download document - returns blob for programmatic use (e.g., merging PDFs)
  async downloadDocument(id: string): Promise<Blob> {
    const response = await api.get(`/api/media/download/${id}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  // Helper to get token from auth store storage locations
  private getToken(): string {
    // Try combined auth format first
    const combinedAuth = localStorage.getItem('auth') || sessionStorage.getItem('auth');
    if (combinedAuth) {
      try {
        const parsed = JSON.parse(combinedAuth);
        if (parsed.token) {
          console.log('✅ Token found in combined auth storage');
          return parsed.token;
        }
      } catch (e) {
        console.warn('Failed to parse combined auth:', e);
      }
    }

    // Try separate token format
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    if (token) {
      console.log('✅ Token found in separate storage');
      return token;
    }

    console.log('❌ No token found in any storage location');
    return '';
  }

  // Get view URL for direct browser viewing
  getViewUrl(id: string): string {
    const token = this.getToken();
    const baseUrl = import.meta.env.VITE_API_BASE_URL || window.location.origin;
    const url = `${baseUrl}/api/media/view/${id}${token ? `?token=${token}` : ''}`;

    console.log('🔍 Generated view URL:', url.replace(token, 'TOKEN_HIDDEN'));
    console.log('🔑 Token exists:', !!token);

    return url;
  }

  // Get download URL for direct download
  getDownloadUrl(id: string): string {
    const token = this.getToken();
    const baseUrl = import.meta.env.VITE_API_BASE_URL || window.location.origin;
    return `${baseUrl}/api/media/download/${id}${token ? `?token=${token}` : ''}`;
  }

  // View document - Try direct URL first, fallback to blob
  async viewDocument(id: string): Promise<void> {
    const token = this.getToken();

    if (token) {
      // If we have a token, try direct URL approach (better performance)
      try {
        const url = this.getViewUrl(id);
        console.log('📤 Opening document with direct URL');
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      } catch (error) {
        console.warn('Direct URL view failed, falling back to blob:', error);
      }
    }

    // Fallback to blob approach (always works with axios)
    console.log('📤 Opening document with blob approach');
    try {
      const blob = await this.downloadDocument(id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error('Error viewing document:', error);
      throw error;
    }
  }

  // Download document using blob approach (most reliable)
  async downloadDocumentDirect(mediaDoc: MediaDocument): Promise<void> {
    try {
      console.log('📥 Downloading document:', mediaDoc.fileName);
      const blob = await this.downloadDocument(mediaDoc.id);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = mediaDoc.fileName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
      console.log('✅ Download started:', mediaDoc.fileName);
    } catch (error) {
      console.error('Error downloading document:', error);
      throw error;
    }
  }

  // Alternative: Try direct download URL (may open in new tab instead of downloading)
  downloadDocumentDirectUrl(mediaDoc: MediaDocument): void {
    const token = this.getToken();
    if (!token) {
      console.warn('No token available for direct download');
      this.downloadDocumentDirect(mediaDoc);
      return;
    }

    const url = this.getDownloadUrl(mediaDoc.id);
    window.open(url, '_blank');
  }
}

export default new MediaService();
